import { useEffect, useRef } from "react";

declare global {
  interface Window {
    instgrm?: { Embeds: { process: () => void } };
  }
}

const SCRIPT_SRC = "https://www.instagram.com/embed.js";

function loadInstagramScript(onReady: () => void) {
  if (typeof document === "undefined") return;
  if (window.instgrm) {
    onReady();
    return;
  }
  let script = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
  if (!script) {
    script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);
  }
  script.addEventListener("load", onReady, { once: true });
}

/** Normalises any Instagram post/reel URL to its canonical permalink. */
export function normalizeInstagramUrl(url: string): string | null {
  try {
    const parsed = new URL(url.trim());
    if (!/(^|\.)instagram\.com$/i.test(parsed.hostname)) return null;
    const match = parsed.pathname.match(/\/(p|reel|reels|tv)\/([A-Za-z0-9_-]+)/);
    if (!match) return null;
    const type = match[1] === "reels" ? "reel" : match[1];
    return `https://www.instagram.com/${type}/${match[2]}/`;
  } catch {
    return null;
  }
}

/**
 * Renders an Instagram post/reel inside article content. Instagram's own
 * embed script replaces the blockquote with the rich iframe once loaded.
 */
export default function InstagramEmbed({ url, caption }: { url: string; caption?: string }) {
  const permalink = normalizeInstagramUrl(url) ?? url;
  const ref = useRef<HTMLQuoteElement>(null);

  useEffect(() => {
    loadInstagramScript(() => window.instgrm?.Embeds.process());
  }, [permalink]);

  return (
    <blockquote
      ref={ref}
      className="instagram-media mx-auto my-8 w-full max-w-[540px] rounded-lg border border-border bg-card p-4"
      data-instgrm-permalink={permalink}
      data-instgrm-version="14"
      data-instgrm-captioned={caption ? "" : undefined}
    >
      <a href={permalink} target="_blank" rel="noopener noreferrer">
        {caption || "View this post on Instagram"}
      </a>
    </blockquote>
  );
}
